import React from "react";
import { motion } from "framer-motion";

const projects = [
  {
    title: "AI-Powered Vaccinator Learning App",
    description:
      "A mobile learning platform for healthcare professionals featuring AI-powered chatbot and voice assistants, course modules, quizzes, and progress tracking to support large-scale vaccination programs.",
    tech: ["React Native", "Node.js", "Flask", "MongoDB", "AI"],
    image: require("../assets/Projects/1.jpg"),
    link: "#",
  },
  {
    title: "Personal Portfolio Website",
    description:
      "A modern, animated personal portfolio built with React and Tailwind CSS, featuring smooth transitions, responsive layouts, and a polished dark-themed design.",
    tech: ["React", "Tailwind CSS", "Framer Motion"],
    image: require("../assets/Projects/2.jpg"),
    link: "https://rameezmurtaza.vercel.app",
  },
  {
    title: "Healthcare Data Analytics Dashboard",
    description:
      "A web-based analytics dashboard designed to visualize large-scale healthcare and vaccine datasets, providing actionable insights through charts, reports, and API-driven data pipelines.",
    tech: ["React", "Node.js", "Express", "MongoDB", "Data Analytics"],
    image: require("../assets/Projects/3.jpg"),
    link: "#",
  },
];

// Animation variants
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const ProjectSection = ({ isFullPage = false }) => {
  return (
    <section
      id="projects"
      className={`relative py-24 ${isFullPage ? "min-h-screen" : ""}`}
    >
      {/* Background glow */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-24 w-72 h-72 bg-blue-500/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-24 w-72 h-72 bg-red-500/15 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-24">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Featured <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            A selection of projects showcasing my work in full-stack
            development, AI-powered systems, and data-driven applications.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {projects.map((project, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -8 }}
              className="group rounded-2xl overflow-hidden glass transition-all"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">{project.title}</h3>

                <p className="text-gray-400 text-sm leading-relaxed mb-5">
                  {project.description}
                </p>

                {/* Tech stack */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tech.map((tech, i) => (
                    <span
                      key={i}
                      className="text-xs px-3 py-1 rounded-full 
                                 bg-white/5 border border-white/10 
                                 text-gray-300"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Button */}
                {project.link && (
                  <motion.a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-block px-5 py-3 rounded-lg text-sm font-medium
                               border border-white/20 bg-white/5
                               hover:bg-white/10 transition-all"
                  >
                    View Project →
                  </motion.a>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectSection;
